// A5 medical certificate / fitness note / referral letter (FR-EMR-8).
// Burmese, English, or both, because employers and other clinics differ.
// Burmese wording to be reviewed by a Myanmar clinician before the pilot.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { ageLabel } from '@/features/patients/repo';
import { formatDate, toMyanmarDigits } from '@/lib/myanmar/digits';
import type { PatientIndexRow } from '@/lib/db/schema';
import type { DocumentRow } from './repo';

interface Props {
  doc: DocumentRow;
  patient: PatientIndexRow;
  doctorName: string | null;
  doctorRegNo: string | null;
  clinicName: string;
  clinicNameAlt: string | null;
  clinicPhone: string | null;
  clinicAddress: string | null;
  onPrinted?: () => void;
  onClose: () => void;
}

const LINE = 'border-b border-dotted border-ink/40';

export function CertificatePrint({ doc, patient, doctorName, doctorRegNo, clinicName, clinicNameAlt, clinicPhone, clinicAddress, onPrinted, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const uiMy = i18n.language === 'my';
  const showMy = doc.language === 'my' || doc.language === 'both';
  const showEn = doc.language === 'en' || doc.language === 'both';

  useEffect(() => {
    const after = () => onPrinted?.();
    window.addEventListener('afterprint', after);
    return () => window.removeEventListener('afterprint', after);
  }, [onPrinted]);

  const age = ageLabel(patient);
  const dateStr = (iso: string | null | undefined, my: boolean) => formatDate(iso, my ? 'my' : 'en');
  const name = (my: boolean) => (my ? (patient.name_my ?? patient.name_en) : (patient.name_en ?? patient.name_my)) ?? '';
  const honorific = patient.honorific && patient.honorific !== 'none' ? patient.honorific : null;

  // Titles and sentences per language, chosen by document kind.
  const title = { my: { medical_certificate: 'ဆေးလက်မှတ်', fitness: 'ကျန်းမာရေး ကောင်းမွန်ကြောင်း လက်မှတ်', referral: 'ညွှန်းပို့စာ', other: 'စာ' },
                  en: { medical_certificate: 'MEDICAL CERTIFICATE', fitness: 'CERTIFICATE OF FITNESS', referral: 'REFERRAL LETTER', other: 'LETTER' } };
  const certify = {
    my: { medical_certificate: 'အထက်ဖော်ပြပါ လူနာအား ကျွန်ုပ်ကိုယ်တိုင် စစ်ဆေးခဲ့ပြီး၊ အလုပ်ဆက်လက် လုပ်ကိုင်ရန် မသင့်သဖြင့် အနားယူရန် ညွှန်ကြားအပ်ပါသည်။',
         fitness: 'အထက်ဖော်ပြပါ လူနာအား ကျွန်ုပ်ကိုယ်တိုင် စစ်ဆေးခဲ့ပြီး၊ ပုံမှန်အလုပ် ပြန်လည်လုပ်ကိုင်နိုင်ပါပြီ။',
         referral: 'အထက်ဖော်ပြပါ လူနာအား ဆက်လက် စစ်ဆေးကုသပေးပါရန် ညွှန်းပို့အပ်ပါသည်။', other: '' },
    en: { medical_certificate: 'This is to certify that I have personally examined the patient named above, who is unfit for duty and is advised to rest as stated below.',
         fitness: 'This is to certify that I have personally examined the patient named above, who is fit to resume normal duties.',
         referral: 'I am referring the patient named above for further examination and treatment.', other: '' },
  };

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-baseline gap-2">
      <span className="shrink-0 text-ink/60">{label}</span>
      <span className={`flex-1 ${LINE} font-semibold`}>{value}</span>
    </div>
  );

  return (
    <div className="grid gap-4">
      <div className="print-rx mx-auto w-full bg-white p-7 text-ink" style={{ maxWidth: '148mm' }}>
        <header className="border-b-2 border-ink/70 pb-2 text-center">
          <div className="text-lg font-bold">{clinicName}</div>
          {clinicNameAlt && <div className="text-sm">{clinicNameAlt}</div>}
          {(clinicAddress || clinicPhone) && <div className="text-xs text-ink/70">{[clinicAddress, clinicPhone].filter(Boolean).join(' · ')}</div>}
        </header>

        <div className="mt-4 text-center">
          {showMy && <div className="text-lg font-bold tracking-wide">{title.my[doc.kind]}</div>}
          {showEn && <div className={showMy ? 'text-sm font-bold tracking-widest' : 'text-lg font-bold tracking-widest'}>{title.en[doc.kind]}</div>}
        </div>

        <div className="mt-3 flex justify-between text-sm">
          <span>{t('doc.no')} <b>{doc.doc_no ?? '—'}</b></span>
          <span>{dateStr(doc.issued_at, showMy && !showEn)}</span>
        </div>

        {doc.recipient && <div className="mt-3 text-sm font-semibold">{doc.recipient}</div>}

        <div className="mt-3 grid gap-1.5 text-sm">
          <Row label={showMy && !showEn ? 'အမည်' : 'Name'} value={`${honorific ? t(`honorific.${honorific}`) + ' ' : ''}${name(showMy)}`} />
          <div className="grid grid-cols-2 gap-3">
            <Row label={showMy && !showEn ? 'အသက်' : 'Age'} value={age ? `${showMy && !showEn ? toMyanmarDigits(age.years) : age.years}` : '—'} />
            <Row label={showMy && !showEn ? 'လူနာအမှတ်' : 'Patient no.'} value={patient.patient_no ?? '—'} />
          </div>
          {patient.nrc && <Row label={showMy && !showEn ? 'မှတ်ပုံတင်' : 'NRC'} value={patient.nrc} />}
        </div>

        <p className="mt-4 text-sm leading-relaxed">
          {showMy && <span className="block">{certify.my[doc.kind]}</span>}
          {showEn && <span className="block">{certify.en[doc.kind]}</span>}
        </p>

        {doc.include_diagnosis && doc.diagnosis_text && (
          <p className="mt-2 text-sm"><span className="text-ink/60">{showMy && !showEn ? 'ရောဂါ' : 'Diagnosis'}: </span><b>{doc.diagnosis_text}</b></p>
        )}

        {doc.kind === 'medical_certificate' && doc.rest_from && (
          <p className="mt-2 text-sm">
            {showMy && <span className="block">
              {dateStr(doc.rest_from, true)} မှ {dateStr(doc.rest_to, true)} အထိ စုစုပေါင်း <b>{toMyanmarDigits(doc.rest_days ?? 0)} ရက်</b> အနားယူရန်။
            </span>}
            {showEn && <span className="block">
              From {dateStr(doc.rest_from, false)} to {dateStr(doc.rest_to, false)}, a total of <b>{doc.rest_days ?? 0} day(s)</b>.
            </span>}
          </p>
        )}

        {doc.kind === 'fitness' && doc.rest_from && (
          <p className="mt-2 text-sm">
            {showMy && <span className="block">အလုပ်ပြန်ဝင်နိုင်သည့်ရက် — <b>{dateStr(doc.rest_from, true)}</b></span>}
            {showEn && <span className="block">Fit to resume work on <b>{dateStr(doc.rest_from, false)}</b></span>}
          </p>
        )}

        {doc.body && <p className="mt-2 whitespace-pre-wrap text-sm">{doc.body}</p>}

        <div className="mt-12 text-right text-sm">
          <div className="inline-block min-w-[52mm] border-t border-ink/60 pt-1 text-center">
            <div className="font-semibold">{doctorName ?? ''}</div>
            {doctorRegNo && <div className="text-xs text-ink/70">{showMy && !showEn ? 'မှတ်ပုံတင်အမှတ်' : 'Reg. no.'} {doctorRegNo}</div>}
          </div>
        </div>

        {doc.status === 'cancelled' && (
          <div className="mt-4 rounded border-2 border-danger px-3 py-1 text-center text-lg font-bold text-danger">
            {showMy ? 'ပယ်ဖျက်ပြီး' : 'CANCELLED'}
          </div>
        )}
      </div>

      <div className="no-print flex items-center justify-between gap-2">
        <span className="text-sm text-muted">{doc.print_count > 0 ? t('doc.printedTimes', { n: doc.print_count }) : ''}</span>
        <div className="flex gap-2">
          <Button onClick={onClose}>{t('common.close')}</Button>
          <Button variant="primary" onClick={() => window.print()}>{uiMy ? t('common.print') : t('common.print')}</Button>
        </div>
      </div>
    </div>
  );
}
